import React, { useEffect, useRef, useState } from 'react';
import { fromLonLat } from 'ol/proj';
import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import { defaults as defaultControls } from 'ol/control';
import 'ol/ol.css';

import bus from '../../../assets/marker_bus.png';
import stopIcon from '../../../assets/marker_stop.png';
// 👇 Tooltip import kiya yahan
import { Box, ToggleButton, Tooltip } from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import haversine from 'haversine';

function Openlayer({ position, thingModel, userLocation, selectedStopId }) {
  const mapEl = useRef(null);
  const popupRef = useRef(null);
  const overlayRef = useRef(null);
  const [map, setMap] = useState(null);
  const [follow, setFollow] = useState(true);

  const liveSrc = useRef(new VectorSource()).current;
  const coveredSrc = useRef(new VectorSource()).current;
  const remainSrc = useRef(new VectorSource()).current;
  const stopsSrc = useRef(new VectorSource()).current;
  const userSrc = useRef(new VectorSource()).current;
  const view = useRef(new View({ zoom: 16, maxZoom: 20 })).current;

  const decodePolyline = (encoded) => {
    if (!encoded?.trim()) return [];
    const poly = [];
    let index = 0, len = encoded.length, lat = 0, lng = 0;
    while (index < len) {
      let b, shift = 0, result = 0;
      do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
      const dlat = result & 1 ? ~(result >> 1) : result >> 1; lat += dlat;
      shift = 0; result = 0;
      do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
      const dlng = result & 1 ? ~(result >> 1) : result >> 1; lng += dlng;
      poly.push([lng / 1e5, lat / 1e5]);
    }
    return poly;
  };

  const drawLine = (coords, color, src) => {
    src.clear();
    if (!coords || coords.length < 2) return;
    const line = new LineString(coords.map(c => fromLonLat(c)));
    const feature = new Feature({ geometry: line });
    feature.setStyle(new Style({ stroke: new Stroke({ color, width: 6, lineCap: 'round' }) }));
    src.addFeature(feature);
  };

  const findClosestPointIndex = (busCoord, polyPoints) => {
    let minDistance = Infinity;
    let closestIndex = 0;

    polyPoints.forEach((point, index) => {
      const dist = haversine(
        { latitude: busCoord.latitude, longitude: busCoord.longitude },
        { latitude: point[1], longitude: point[0] },
        { unit: 'meter' }
      );
      if (dist < minDistance) {
        minDistance = dist;
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  useEffect(() => {
    if (!mapEl.current) return;
    const olMap = new Map({
      target: mapEl.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({ source: coveredSrc, zIndex: 10 }),
        new VectorLayer({ source: remainSrc, zIndex: 11 }),
        new VectorLayer({ source: stopsSrc, zIndex: 12 }),
        new VectorLayer({ source: userSrc, zIndex: 13 }),
        new VectorLayer({ source: liveSrc, zIndex: 14 })
      ],
      view,
      controls: defaultControls({ attribution: false, zoom: true })
    });

    const popupOverlay = new Overlay({
      element: popupRef.current,
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -25],
    });
    olMap.addOverlay(popupOverlay);
    overlayRef.current = popupOverlay;

    olMap.on('singleclick', (evt) => {
      const feature = olMap.forEachFeatureAtPixel(evt.pixel, (feat) => feat);
      if (feature && feature.get('isStop')) {
        const coordinates = feature.getGeometry().getCoordinates();
        const stopName = feature.get('stopName');
        popupRef.current.innerHTML = stopName || 'Unknown Stop';
        popupRef.current.style.display = 'block';
        popupOverlay.setPosition(coordinates);
      } else {
        popupOverlay.setPosition(undefined);
        popupRef.current.style.display = 'none';
      }
    });

    olMap.on('pointermove', (evt) => {
      const pixel = olMap.getEventPixel(evt.originalEvent);
      const hit = olMap.hasFeatureAtPixel(pixel, {
        layerFilter: (layer) => layer.getSource() === stopsSrc
      });
      olMap.getTargetElement().style.cursor = hit ? 'pointer' : '';
    });

    setMap(olMap);
    return () => olMap.setTarget(undefined);
  }, [stopsSrc, coveredSrc, remainSrc, userSrc, liveSrc, view]);

  useEffect(() => {
    if (!map || !position || !thingModel) return;
    const lat = position.latitude ?? position.lat;
    const lng = position.longitude ?? position.lng;
    const coord = fromLonLat([lng, lat]);

    // Update Bus Marker
    liveSrc.clear();
    const busF = new Feature({ geometry: new Point(coord) });
    busF.setStyle(new Style({ image: new Icon({ anchor: [0.5, 1], src: bus, scale: 0.35 }) }));
    liveSrc.addFeature(busF);

    // === FIX: Sorted Route Stops Sequence exactly like GoogleMap logic ===
    stopsSrc.clear();
    const stops = thingModel.stops || {};
    const sortedStopsArray = Object.values(stops)
      .filter((s) => s.location?.latitude && s.location?.longitude)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0)); // Match standard sort sequence

    sortedStopsArray.forEach((stop, i) => {
      const f = new Feature({
        geometry: new Point(fromLonLat([stop.location.longitude, stop.location.latitude])),
        isStop: true,
        stopName: stop.locationName
      });
      f.setStyle(new Style({
        image: new Icon({ anchor: [0.5, 1], src: stopIcon, scale: 0.3 }),
        text: new Text({
          text: (i + 1).toString(), // Absolute sequential index layout
          offsetY: -15,
          fill: new Fill({ color: '#fff' }),
          stroke: new Stroke({ color: '#000', width: 2 }),
          font: 'bold 12px sans-serif'
        })
      }));
      stopsSrc.addFeature(f);
    });

    // User Location
    userSrc.clear();
    if (userLocation?.latitude) {
      const uCoord = fromLonLat([userLocation.longitude, userLocation.latitude]);
      const userF = new Feature({ geometry: new Point(uCoord) });
      userF.setStyle(new Style({ image: new Circle({ radius: 8, fill: new Fill({ color: '#1976d2' }), stroke: new Stroke({ color: '#fff', width: 3 }) }) }));
      userSrc.addFeature(userF);
    }

    // Dynamic Polyline Splitting Logic
    // const poly = thingModel.currentJourney?.wayPointsPolyLine;
    // if (poly) {
    //   const pts = decodePolyline(poly);

    //   if (pts.length > 0) {
    //     const busLocationObject = { latitude: lat, longitude: lng };
    //     const closestIndex = findClosestPointIndex(busLocationObject, pts);

    //     const coveredPoints = pts.slice(0, closestIndex + 1);
    //     coveredPoints.push([lng, lat]); 

    //     const remainingPoints = pts.slice(closestIndex);
    //     remainingPoints.unshift([lng, lat]);

    //     // Covered Route Line to Green
    //     drawLine(coveredPoints, '#2e7d32', coveredSrc);

    //     // Remaining Route Line to Red
    //     drawLine(remainingPoints, '#f04c31', remainSrc);
    //   }
    // } else {
    //   coveredSrc.clear();
    //   remainSrc.clear();
    // }

    // Dynamic Polyline Splitting Logic
    const poly = thingModel.currentJourney?.wayPointsPolyLine;
    if (poly) {
      const pts = decodePolyline(poly);

      if (pts.length > 0) {
        const busLocationObject = { latitude: lat, longitude: lng };
        const closestIndex = findClosestPointIndex(busLocationObject, pts);

        const closestPoint = pts[closestIndex];

        // Calculate distance between bus and the closest point on the route
        const distToRoute = haversine(
          { latitude: lat, longitude: lng },
          { latitude: closestPoint[1], longitude: closestPoint[0] },
          { unit: 'meter' }
        );

        const coveredPoints = pts.slice(0, closestIndex + 1);
        const remainingPoints = pts.slice(closestIndex);

        // Agar bus route se 300 meters ke andar hai, tabhi line bus se connect karein
        // Varna straight line draw karne se bachein
        if (distToRoute < 300) {
          coveredPoints.push([lng, lat]);
          remainingPoints.unshift([lng, lat]);
        }

        // Covered Route Line to Green
        drawLine(coveredPoints, '#2e7d32', coveredSrc);

        // Remaining Route Line to Red
        drawLine(remainingPoints, '#f04c31', remainSrc);
      }
    } else {
      coveredSrc.clear();
      remainSrc.clear();
    }

    if (follow) view.animate({ center: coord, duration: 800 });
  }, [position, thingModel, userLocation, follow, map, liveSrc, stopsSrc, userSrc, coveredSrc, remainSrc, view]);

  return (
    <Box sx={{ width: '100%', height: '100vh', position: 'relative' }}>
      <Box ref={mapEl} sx={{ width: '100%', height: '100%' }} />

      <div
        ref={popupRef}
        style={{
          display: 'none',
          position: 'absolute',
          backgroundColor: 'white',
          color: '#333',
          padding: '6px 12px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          boxShadow: '0px 4px 10px rgba(0,0,0,0.3)',
          border: '1px solid #ddd',
          pointerEvents: 'none',
          transform: 'translate(-50%, -100%)',
          zIndex: 100
        }}
      />

      {/* 👇 Yahan Tooltip lagaya gaya hai */}
      <Tooltip title={follow ? "Follow : ON" : "Follow : OFF"} placement="right" arrow>
        <ToggleButton
          value="check"
          selected={follow}
          onChange={() => setFollow(!follow)}
          sx={{
            position: 'absolute',
            top: 70,
            left: 16,
            bgcolor: '#fff',      // OFF state background
            color: 'gray',         // OFF state icon color
            borderRadius: 2,
            boxShadow: 3,

            // 1. Normal Hover (Jab OFF ho)
            '&:hover': {
              bgcolor: 'white',    // Hover karne par bhi completely white rahega
            },

            // 2. Selected State (Jab ON ho)
            '&.Mui-selected': {
              bgcolor: '#e3f2fd',  // Light blue background jab ON ho
              color: '#1976d2',    // Dark blue icon jab ON ho

              // 3. Selected Hover (Jab ON ho aur hover karein)
              '&:hover': {
                bgcolor: '#bbdefb', // Hover par thoda dark blue, NO pink
              },
            },
          }}
        >
          <LocationSearchingIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
    </Box>
  );
}

export default Openlayer;